import { useState } from 'react';
import toast from 'react-hot-toast';
import { PLANS, type Plan } from '@seencast/shared';
import { useAdminOrgs, useCreateAdminOrg, useUpdateAdminOrg } from '../../api/admin.ts';
import { usePermissions } from '../../auth/session.tsx';
import { Button, Badge, Loading, ErrorState, TextField, SelectField, Field } from '../../components/ui.tsx';
import { Modal } from '../../components/Modal.tsx';
import { CopyField } from '../settings/CopyField.tsx';
import type { AdminOrgListItem } from '@seencast/shared';
import { formatMb, titleCase } from '../../lib/format.ts';
import { ApiError } from '../../api/client.ts';

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function CreateOrgModal({ onClose }: { onClose: () => void }) {
  const create = useCreateAdminOrg();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [plan, setPlan] = useState<Plan>('trial');
  const [maxScreens, setMaxScreens] = useState(5);
  const [maxStorage, setMaxStorage] = useState(2000);
  const [assistant, setAssistant] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const effectiveSlug = slug || slugify(name);
  const canSubmit = name.trim() && effectiveSlug && ownerEmail.includes('@');

  function submit() {
    create.mutate(
      {
        name: name.trim(),
        slug: effectiveSlug,
        plan,
        limits: { max_screens: maxScreens, max_storage_mb: maxStorage, assistant },
        owner_email: ownerEmail.trim(),
      },
      {
        onSuccess: (res) => {
          setInviteUrl(res.invite_url);
          toast.success('Organization created');
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
      },
    );
  }

  return (
    <Modal
      title="Create organization"
      onClose={onClose}
      size="lg"
      footer={
        inviteUrl ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={submit} disabled={!canSubmit || create.isPending}>
              Create
            </Button>
          </>
        )
      }
    >
      {inviteUrl ? (
        <div className="hint-box info">
          Organization created. Send the owner their invite link:
          <div className="mt-2">
            <CopyField value={inviteUrl} label="Owner invite link" />
          </div>
        </div>
      ) : (
        <>
          <div className="field-row">
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Retail" />
            <TextField label="Slug" value={effectiveSlug} onChange={(e) => setSlug(slugify(e.target.value))} hint="Lowercase, for white-label" />
          </div>
          <div className="field-row">
            <SelectField label="Plan" value={plan} onChange={(e) => setPlan(e.target.value as Plan)}>
              {PLANS.map((p) => (
                <option key={p} value={p}>
                  {titleCase(p)}
                </option>
              ))}
            </SelectField>
            <TextField label="Owner email" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@acme.com" />
          </div>
          <div className="field-row">
            <TextField label="Max screens" type="number" value={maxScreens} onChange={(e) => setMaxScreens(Number(e.target.value))} />
            <TextField label="Max storage (MB)" type="number" value={maxStorage} onChange={(e) => setMaxStorage(Number(e.target.value))} />
          </div>
          <Field label="Assistant">
            <label className="row">
              <input type="checkbox" checked={assistant} onChange={(e) => setAssistant(e.target.checked)} />
              <span className="text-dim">Enable the AI assistant for this org</span>
            </label>
          </Field>
        </>
      )}
    </Modal>
  );
}

function EditLimitsModal({ org, onClose }: { org: AdminOrgListItem; onClose: () => void }) {
  const update = useUpdateAdminOrg();
  const [plan, setPlan] = useState<Plan>(org.plan);
  const [maxScreens, setMaxScreens] = useState(org.limits.max_screens);
  const [maxStorage, setMaxStorage] = useState(org.limits.max_storage_mb);
  const [assistant, setAssistant] = useState(org.limits.assistant);

  return (
    <Modal
      title={`Edit ${org.name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                { id: org.id, body: { plan, limits: { max_screens: maxScreens, max_storage_mb: maxStorage, assistant } } },
                {
                  onSuccess: () => {
                    toast.success('Updated');
                    onClose();
                  },
                  onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
                },
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      <SelectField label="Plan" value={plan} onChange={(e) => setPlan(e.target.value as Plan)}>
        {PLANS.map((p) => (
          <option key={p} value={p}>
            {titleCase(p)}
          </option>
        ))}
      </SelectField>
      <div className="field-row">
        <TextField label="Max screens" type="number" value={maxScreens} onChange={(e) => setMaxScreens(Number(e.target.value))} />
        <TextField label="Max storage (MB)" type="number" value={maxStorage} onChange={(e) => setMaxStorage(Number(e.target.value))} />
      </div>
      <Field label="Assistant">
        <label className="row">
          <input type="checkbox" checked={assistant} onChange={(e) => setAssistant(e.target.checked)} />
          <span className="text-dim">Enabled</span>
        </label>
      </Field>
    </Modal>
  );
}

export function AdminPage() {
  const perms = usePermissions();
  const orgs = useAdminOrgs();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AdminOrgListItem | null>(null);

  if (!perms.isSuperadmin) {
    return (
      <div className="page">
        <ErrorState message="This area is for SeenLabs staff only." />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>SeenLabs Admin</h1>
          <div className="subtitle">All customer organizations, plans, and usage.</div>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          Create organization
        </Button>
      </div>

      {orgs.isLoading ? (
        <Loading />
      ) : orgs.isError ? (
        <ErrorState message="Could not load organizations." onRetry={() => orgs.refetch()} />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Plan</th>
                <th>Screens</th>
                <th>Storage</th>
                <th>Members</th>
                <th>Owner</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orgs.data!.map((o) => (
                <tr key={o.id}>
                  <td>
                    <div className="cell-strong">{o.name}</div>
                    <div className="cell-dim mono">{o.slug}</div>
                  </td>
                  <td>
                    <Badge tone="info">{titleCase(o.plan)}</Badge>
                  </td>
                  <td>
                    {o.usage.screens} / {o.limits.max_screens}
                  </td>
                  <td>
                    {formatMb(o.usage.storage_mb)} / {formatMb(o.limits.max_storage_mb)}
                  </td>
                  <td>{o.member_count}</td>
                  <td className="cell-dim">{o.owner_email ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Button size="sm" onClick={() => setEditing(o)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateOrgModal onClose={() => setCreating(false)} />}
      {editing && <EditLimitsModal org={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
